import { useEffect, useRef, useState } from 'react'
import BpmnViewer from 'bpmn-js/lib/Viewer'
import { socketService } from '@/services/socketService'

interface BPMNLiveViewerProps {
  conversationId: string
  initialBpmn?: string
  showControls?: boolean
}

export default function BPMNLiveViewer({ 
  conversationId, 
  initialBpmn, 
  showControls = true 
}: BPMNLiveViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const viewerRef = useRef<BpmnViewer | null>(null)
  const [currentBpmn, setCurrentBpmn] = useState<string | undefined>(initialBpmn)
  const [highlightedElements, setHighlightedElements] = useState<string[]>([])
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showExportMenu, setShowExportMenu] = useState(false)

  // Log when initialBpmn changes
  useEffect(() => {
    console.log('BPMNLiveViewer received initialBpmn:', {
      hasInitialBpmn: !!initialBpmn,
      length: initialBpmn?.length,
      preview: initialBpmn?.substring(0, 100)
    })
    if (initialBpmn) {
      setCurrentBpmn(initialBpmn)
      setLastUpdate(new Date())
    }
  }, [initialBpmn])

  // Handle click outside to close export menu
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showExportMenu && !(e.target as Element).closest('.export-menu-container')) {
        setShowExportMenu(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showExportMenu])

  // Initialize BPMN viewer
  useEffect(() => {
    if (!containerRef.current) return

    viewerRef.current = new BpmnViewer({
      container: containerRef.current,
      height: '100%',
      width: '100%',
    })

    return () => {
      viewerRef.current?.destroy()
    }
  }, [])

  // Load BPMN diagram
  useEffect(() => {
    console.log('BPMNLiveViewer loadDiagram effect:', {
      hasViewer: !!viewerRef.current,
      hasCurrentBpmn: !!currentBpmn,
      bpmnLength: currentBpmn?.length
    })
    
    if (!viewerRef.current || !currentBpmn) return

    const loadDiagram = async () => {
      try {
        console.log('Loading BPMN diagram into viewer...')
        await viewerRef.current!.importXML(currentBpmn)
        
        // Auto-fit diagram
        const canvas = viewerRef.current!.get('canvas') as any
        canvas.zoom('fit-viewport', 'auto')
        console.log('BPMN diagram loaded successfully')
        
        // Highlight new elements
        if (highlightedElements.length > 0) {
          const elementRegistry = viewerRef.current!.get('elementRegistry') as any
          
          highlightedElements.forEach(elementId => {
            const element = elementRegistry.get(elementId)
            if (element) {
              // Add highlighting (green glow)
              canvas.addMarker(elementId, 'highlight-new')
              
              // Remove highlight after 3 seconds
              setTimeout(() => {
                canvas.removeMarker(elementId, 'highlight-new')
              }, 3000)
            }
          })
        }
        
        setError(null)
      } catch (err: any) {
        console.error('Failed to load BPMN diagram:', err)
        setError(`Failed to load BPMN diagram: ${err.message || err}`)
      }
    }

    loadDiagram()
  }, [currentBpmn, highlightedElements])

  // Subscribe to WebSocket updates
  useEffect(() => {
    const handleBPMNUpdate = (data: any) => {
      if (data.conversationId !== conversationId) return
      
      setCurrentBpmn(data.bpmn)
      setLastUpdate(new Date())
      
      // Highlight newly added elements
      if (data.delta) {
        const newElements = [
          ...(data.delta.blocksAdded || []),
          ...(data.delta.flowsAdded || [])
        ]
        setHighlightedElements(newElements)
      }
    }

    socketService.on('bpmn:update', handleBPMNUpdate)

    return () => {
      socketService.off('bpmn:update', handleBPMNUpdate)
    }
  }, [conversationId])

  const handleZoomIn = () => {
    const canvas = viewerRef.current?.get('canvas') as any
    canvas?.zoom(1.2)
  }

  const handleZoomOut = () => {
    const canvas = viewerRef.current?.get('canvas') as any
    canvas?.zoom(0.8)
  }

  const handleZoomReset = () => {
    const canvas = viewerRef.current?.get('canvas') as any
    canvas?.zoom('fit-viewport', 'auto')
  }

  const handleExport = async (format: 'standard' | 'mobifin' | 'json' = 'standard') => {
    if (!conversationId) return
    
    let exportContent = currentBpmn
    let fileName = `workflow-${conversationId}`
    let mimeType = 'text/xml'
    
    if (format === 'mobifin') {
      // Fetch MobiFin-specific BPMN from backend
      try {
        const API_BASE = import.meta.env.VITE_API_BASE_URL || ''
        const response = await fetch(`${API_BASE}/api/v1/conversations/${conversationId}/bpmn?compatibility=mobifin&format=xml`)
        const responseText = await response.text()
        
        // Extract just the BPMN content if the response contains more than XML
        if (responseText.includes('<?xml')) {
          // If it's a JSON response with bpmn field, extract the bpmn
          try {
            const jsonResponse = JSON.parse(responseText)
            if (jsonResponse.bpmn) {
              exportContent = jsonResponse.bpmn
            } else {
              exportContent = responseText
            }
          } catch {
            // If it's already XML, use as is
            exportContent = responseText
          }
        } else {
          exportContent = responseText
        }
      } catch (error) {
        console.error('Failed to fetch MobiFin BPMN:', error)
      }
      
      fileName += '-mobifin.bpmn'
      
      // Add MobiFin-specific header comment
      const mobifinHeader = `<?xml version="1.0" encoding="UTF-8"?>
<!-- MobiFin Workflow Export -->
<!-- Generated by Tapestry BPMN Builder -->
<!-- Conversation ID: ${conversationId} -->
<!-- Export Date: ${new Date().toISOString()} -->
`
      exportContent = exportContent?.replace('<?xml version="1.0" encoding="UTF-8"?>', mobifinHeader) || ''
    } else if (format === 'json') {
      // Export as MobiFin ICJ (Internal Canonical JSON)
      fileName += '-mobifin.json'
      mimeType = 'application/json'
      
      // Extract MobiFin blocks and flows from BPMN
      const blocks: any[] = []
      const flows: any[] = []
      
      // Parse BPMN to extract MobiFin metadata
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(currentBpmn || '', 'text/xml')
      
      // Extract service tasks and events
      const elements = xmlDoc.querySelectorAll('*[UID]')
      elements.forEach((element) => {
        const uid = element.getAttribute('UID')
        const taskName = element.getAttribute('Task_Name')
        const blockCategory = element.getAttribute('BlockCategory')
        const id = element.getAttribute('id')
        const name = element.getAttribute('name')
        
        if (uid && uid !== 'SEQUENCEFLOW') {
          blocks.push({
            id: id,
            blockId: uid,
            name: name || taskName,
            category: blockCategory,
            metadata: {
              taskName,
              elementId: id
            }
          })
        }
      })
      
      // Extract sequence flows
      const sequenceFlows = xmlDoc.querySelectorAll('sequenceFlow')
      sequenceFlows.forEach((flow) => {
        flows.push({
          id: flow.getAttribute('id'),
          source: flow.getAttribute('sourceRef'),
          target: flow.getAttribute('targetRef'),
          condition: flow.getAttribute('name') || undefined
        })
      })
      
      exportContent = JSON.stringify({
        conversationId,
        exportDate: new Date().toISOString(),
        format: 'MobiFin ICJ',
        workflow: {
          blocks,
          flows,
          metadata: {
            totalBlocks: blocks.length,
            totalFlows: flows.length
          }
        }
      }, null, 2)
    } else {
      // Standard BPMN format: Remove MobiFin-specific attributes
      fileName += '.bpmn'
      
      // Remove MobiFin-specific attributes for standard export
      exportContent = (exportContent || '')
        .replace(/\sUID="[^"]*"/g, '')
        .replace(/\sBlockCategory="[^"]*"/g, '')
        .replace(/\sTask_Name="[^"]*"/g, '')
    }
    
    const blob = new Blob([exportContent], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-lg overflow-hidden">
      {showControls && (
        <div className="flex-shrink-0 border-b px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <h3 className="text-sm font-semibold">BPMN Live View</h3>
              {lastUpdate && (
                <span className="text-xs text-gray-500">
                  Last update: {lastUpdate.toLocaleTimeString()}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleZoomOut}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Zoom Out"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
              </button>
              
              <button
                onClick={handleZoomReset}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Fit to View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              </button>
              
              <button
                onClick={handleZoomIn}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
                title="Zoom In"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                </svg>
              </button>
              
              <div className="w-px h-6 bg-gray-300 mx-1" />
              
              <div className="relative export-menu-container">
                <button
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  className="px-3 py-1 text-sm bg-[#0066CC] text-white rounded hover:bg-[#004499] transition-colors flex items-center space-x-1"
                >
                  <span>Export</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showExportMenu && (
                  <div className="absolute right-0 mt-1 w-56 bg-white rounded-md shadow-lg z-10 border">
                    <button
                      onClick={() => {
                        handleExport('standard')
                        setShowExportMenu(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors"
                    >
                      <div className="font-medium">Standard BPMN 2.0</div>
                      <div className="text-xs text-gray-500">Clean BPMN XML format</div>
                    </button>
                    <button
                      onClick={() => {
                        handleExport('mobifin')
                        setShowExportMenu(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors border-t"
                    >
                      <div className="font-medium">MobiFin BPMN</div>
                      <div className="text-xs text-gray-500">BPMN with MobiFin attributes</div>
                    </button>
                    <button
                      onClick={() => {
                        handleExport('json')
                        setShowExportMenu(false)
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition-colors border-t"
                    >
                      <div className="font-medium">MobiFin JSON (ICJ)</div>
                      <div className="text-xs text-gray-500">Internal Canonical JSON format</div>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex-1 relative overflow-auto min-h-0">
        <div ref={containerRef} className="w-full h-full min-h-[400px]" />
        
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-90">
            <div className="text-center">
              <p className="text-red-600 mb-2">{error}</p>
              <p className="text-sm text-gray-600">Waiting for valid BPMN...</p>
            </div>
          </div>
        )}
        
        {!currentBpmn && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="animate-pulse mb-4">
                <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <p className="text-gray-600">Waiting for BPMN diagram...</p>
              <p className="text-sm text-gray-500 mt-1">Start a conversation to see the live view</p>
            </div>
          </div>
        )}
      </div>
      
      <style>{`
        :global(.highlight-new) {
          stroke: #10B981 !important;
          stroke-width: 3px !important;
          filter: drop-shadow(0 0 4px rgba(16, 185, 129, 0.5));
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
      `}</style>
    </div>
  )
}